/**
 * Glass-material bottom navigation bar.
 * Handles three modes: tabs (main navigation), search (input field),
 * and detail (contact detail with sub-tabs).
 */
import { useRef, useEffect } from "react";
import { cn } from "@/lib/cn";
import { Search, ArrowLeft, ShieldCheck, Clock, Pencil, Lock, X } from "lucide-react";
import { MeIcon } from "@/icons/MeIcon";
import {
  PeopleIcon,
  CirclesIcon,
  PathsIcon,
  OverviewIcon,
  DetailsIcon,
  InteractionsIcon,
} from "@/icons/NavIcons";

export type Tab = "me" | "people" | "circles" | "paths";
export type DetailTab = "overview" | "details" | "circles" | "interactions";
export type NavMode = "tabs" | "search" | "detail";

const tabColors: Record<Tab, string> = {
  me: "#E8675A",
  people: "#5B9BD5",
  circles: "#A07FD8",
  paths: "#4EAE7A",
};

const detailTabColors: Record<DetailTab, string> = {
  overview: "#E8675A",
  details: "#5B9BD5",
  circles: "#A07FD8",
  interactions: "#4EAE7A",
};

type CustomIcon = typeof PeopleIcon;

const mainTabDefs: { key: Tab; label: string; icon: CustomIcon | null }[] = [
  { key: "me", label: "Me", icon: null },
  { key: "people", label: "People", icon: PeopleIcon },
  { key: "circles", label: "Circles", icon: CirclesIcon },
  { key: "paths", label: "Paths", icon: PathsIcon },
];

const detailTabDefs: { key: DetailTab; label: string; icon: CustomIcon }[] = [
  { key: "overview", label: "Overview", icon: OverviewIcon },
  { key: "details", label: "Details", icon: DetailsIcon },
  { key: "circles", label: "Circles", icon: CirclesIcon },
  { key: "interactions", label: "Interactions", icon: InteractionsIcon },
];

const glass =
  "bg-[rgba(30,30,30,0.65)] border border-[rgba(255,255,255,0.08)] [backdrop-filter:blur(50px)_saturate(180%)] shadow-[0_4px_24px_rgba(0,0,0,0.3),inset_0_0.5px_0_rgba(255,255,255,0.08)]";

const H = 52;

function TabBtn({
  icon: Icon,
  label,
  active,
  color,
  onClick,
  isMe,
  disabled,
}: {
  icon: CustomIcon | null;
  label: string;
  active: boolean;
  color: string;
  onClick: () => void;
  isMe?: boolean;
  disabled?: boolean;
}) {
  const iconColor = disabled
    ? "rgba(255,255,255,0.12)"
    : active
      ? color
      : "rgba(255,255,255,0.4)";
  return (
    <button
      onClick={disabled ? undefined : onClick}
      className={`flex flex-1 flex-col items-center justify-center gap-[3px] ${disabled ? "pointer-events-none" : "active:opacity-60"}`}
    >
      {isMe ? (
        <MeIcon size={21} strokeWidth={active ? 2.2 : 1.5} style={{ color: iconColor, transition: "color 0.15s" }} />
      ) : Icon ? (
        <Icon size={21} strokeWidth={active ? 2.2 : 1.5} style={{ color: iconColor, transition: "color 0.15s" }} />
      ) : null}
      <span className="text-[10px] font-medium" style={{ color: iconColor, transition: "color 0.15s" }}>
        {label}
      </span>
    </button>
  );
}

export type IdentityState = "local" | "verified" | "pending" | null;

interface NavBarProps {
  mode: NavMode;
  activeTab: Tab;
  activeDetailTab: DetailTab;
  onTabChange: (tab: Tab) => void;
  onDetailTabChange: (tab: DetailTab) => void;
  onSearchToggle: () => void;
  onBack: () => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  identityState?: IdentityState;
  keyboardOpen?: boolean;
  keyboardHeight?: number;
  isSearchMode?: boolean;
}

export function NavBar({
  mode,
  activeTab,
  activeDetailTab,
  onTabChange,
  onDetailTabChange,
  onSearchToggle,
  onBack,
  searchQuery,
  onSearchChange,
  identityState,
  keyboardOpen,
  keyboardHeight = 0,
  isSearchMode,
}: NavBarProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  const isSearch = mode === "search";
  const isDetail = mode === "detail";
  const isTabs = mode === "tabs";

  const activeTabDef = mainTabDefs.find((t) => t.key === activeTab);
  const ActiveTabIcon = activeTabDef?.icon;
  const isActiveMe = activeTab === "me";

  useEffect(() => {
    if (isSearch && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 320);
    }
  }, [isSearch]);

  const tabsLocked = isDetail && identityState === "pending";
  const hidden = keyboardOpen ?? false;
  const searchDocked = isSearchMode && keyboardHeight > 0;

  return (
    <>
      {/* Gradient fade */}
      <div
        className="pointer-events-none fixed bottom-0 left-0 right-0 z-40 transition-opacity duration-200"
        style={{
          background: "linear-gradient(to bottom, transparent, rgba(20,20,24,0.4), var(--color-background))",
          opacity: hidden ? 0 : 1,
          height: searchDocked ? keyboardHeight + 100 : 160,
          bottom: searchDocked ? keyboardHeight - 40 : 0,
        }}
      />

      {/* Identity status strip */}
      {isDetail && identityState && !hidden && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 px-4 transition-all duration-200"
          style={{ paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${H + 16}px)` }}
        >
          {identityState === "verified" && (
            <div className="flex items-center justify-center gap-1.5 rounded-full border border-[rgba(78,174,122,0.12)] bg-[rgba(78,174,122,0.06)] px-3 py-1.5 [backdrop-filter:blur(20px)]">
              <ShieldCheck size={12} className="text-[#4EAE7A]" />
              <span className="text-[11px] font-medium text-[#4EAE7A]">Verified</span>
              <span className="text-[10px] text-[rgba(78,174,122,0.5)]">·</span>
              <Lock size={10} className="text-[rgba(78,174,122,0.4)]" />
              <span className="text-[10px] text-[rgba(78,174,122,0.5)]">Profile controlled by them</span>
            </div>
          )}
          {identityState === "pending" && (
            <div className="flex items-center justify-center gap-1.5 rounded-full border border-[rgba(201,168,76,0.12)] bg-[rgba(201,168,76,0.06)] px-3 py-1.5 [backdrop-filter:blur(20px)]">
              <Clock size={12} className="text-primary" />
              <span className="text-primary text-[11px] font-medium">Pending</span>
              <span className="text-[10px] text-[rgba(201,168,76,0.5)]">·</span>
              <span className="text-[10px] text-[rgba(201,168,76,0.5)]">Waiting for them to accept</span>
            </div>
          )}
          {identityState === "local" && (
            <div className="flex items-center justify-center gap-1.5 rounded-full border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.03)] px-3 py-1.5 [backdrop-filter:blur(20px)]">
              <Pencil size={11} className="text-muted-foreground" />
              <span className="text-muted-foreground text-[11px] font-medium">Local</span>
              <span className="text-[10px] text-[rgba(255,255,255,0.15)]">·</span>
              <span className="text-[10px] text-[rgba(255,255,255,0.25)]">Not on Meridian — you can edit</span>
            </div>
          )}
        </div>
      )}

      <div
        className="fixed left-0 right-0 z-50 flex items-center gap-2 px-4 transition-all duration-200"
        style={{
          bottom: searchDocked ? keyboardHeight : 0,
          paddingBottom: searchDocked ? 8 : `calc(env(safe-area-inset-bottom, 0px) + 10px)`,
          height: H + (searchDocked ? 16 : 20),
          transform: hidden ? "translateY(100%)" : "translateY(0)",
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? "none" : "auto",
        }}
      >
        {/* LEFT ELEMENT */}
        <div
          className={cn(glass, "flex items-center overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]")}
          style={{
            height: H,
            flex: isTabs ? 1 : "0 0 auto",
            width: isTabs ? undefined : H,
            borderRadius: isTabs ? 26 : H / 2,
            paddingLeft: isTabs ? 4 : 0,
            paddingRight: isTabs ? 4 : 0,
          }}
        >
          {isTabs ? (
            mainTabDefs.map((tab) => (
              <TabBtn
                key={tab.key}
                icon={tab.icon}
                label={tab.label}
                active={activeTab === tab.key}
                color={tabColors[tab.key]}
                onClick={() => onTabChange(tab.key)}
                isMe={tab.key === "me"}
              />
            ))
          ) : (
            <button
              onClick={isSearch ? onSearchToggle : onBack}
              className="flex h-full w-full items-center justify-center active:opacity-60"
              style={{ color: isDetail ? "rgba(255,255,255,0.5)" : tabColors[activeTab] }}
            >
              {isDetail ? (
                <ArrowLeft size={21} strokeWidth={1.5} />
              ) : isSearch ? (
                <X size={21} strokeWidth={1.5} />
              ) : isActiveMe ? (
                <MeIcon size={21} strokeWidth={1.5} />
              ) : ActiveTabIcon ? (
                <ActiveTabIcon size={21} strokeWidth={1.5} />
              ) : null}
            </button>
          )}
        </div>

        {/* RIGHT ELEMENT */}
        <div
          className={cn(glass, "flex items-center overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]")}
          style={{
            height: H,
            flex: isTabs ? "0 0 auto" : 1,
            width: isTabs ? H : undefined,
            borderRadius: isTabs ? H / 2 : 26,
            paddingLeft: isTabs ? 0 : isSearch ? 16 : 4,
            paddingRight: isTabs ? 0 : isSearch ? 16 : 4,
          }}
        >
          {isTabs ? (
            <button
              onClick={onSearchToggle}
              className="flex h-full w-full items-center justify-center text-[rgba(255,255,255,0.5)] active:opacity-60"
            >
              <Search size={21} strokeWidth={1.5} />
            </button>
          ) : isSearch ? (
            <>
              <Search size={17} className="mr-2.5 shrink-0 text-[rgba(255,255,255,0.25)]" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search people, groups..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="h-full w-full bg-transparent text-[15px] font-light text-white placeholder:text-[rgba(255,255,255,0.22)] focus:outline-none"
              />
            </>
          ) : (
            detailTabDefs.map((tab) => (
              <TabBtn
                key={tab.key}
                icon={tab.icon}
                label={tab.label}
                active={activeDetailTab === tab.key}
                color={detailTabColors[tab.key]}
                onClick={() => onDetailTabChange(tab.key)}
                disabled={tabsLocked && tab.key !== "overview"}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}
